export default {
  // Common
  common: {
    loading: 'Loading...',
    status: 'Status',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    confirm: 'Confirm',
    search: 'Search',
    refresh: 'Refresh',
    create: 'Create',
    start: 'Start',
    stop: 'Stop',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
    error: 'Error',
    success: 'Success',
    warning: 'Warning',
    info: 'Info',
    copy: 'Copy',
    viewDetails: 'View Details',
    none: 'None',
    copyFailed: 'Copy failed, please copy manually',
    sshCommandCopied: 'SSH command copied to clipboard',
    unknown: 'Unknown'
  },

  // Navigation and menu
  navigation: {
    topology: 'Topology View',
    list: 'List View',
    audit: 'Audit Log',
    gcpManager: 'GCP Manager',
    awsManager: 'AWS Manager',
    projectId: 'Project ID',
    loadResources: 'Load Resources',
    loadPrompt: 'Click the button below to load project resources'
  },

  // Statistics
  stats: {
    virtualMachines: 'Virtual Machines',
    disks: 'Disks',
    createVM: 'Create VM',
    createDisk: 'Create Disk'
  },

  // User info
  user: {
    role: 'Role',
    viewer: 'Viewer',
    editor: 'Editor',
    admin: 'Admin'
  },

  // Filters
  filters: {
    searchPlaceholder: 'Search VMs or Disks...',
    showRunningOnly: 'Show running VMs only'
  },

  // VM related
  vm: {
    name: 'VM Name',
    zone: 'Zone',
    machineType: 'Machine Type',
    cpu: 'CPU',
    memory: 'Memory',
    gpu: 'GPU',
    systemDisk: 'System Disk',
    running: 'Running',
    stopped: 'Stopped',
    starting: 'Starting',
    stopping: 'Stopping',
    shuttingDown: 'Shutting Down',
    terminated: 'Terminated',
    provisioning: 'Provisioning',
    staging: 'Staging',
    repair: 'Repairing',
    pending: 'Pending',
    unknown: 'Unknown',
    startVM: 'Start VM',
    stopVM: 'Stop VM',
    deleteVM: 'Delete VM',
    editLabels: 'Edit Labels',
    // VM resize related
    resizeVM: {
      title: 'Resize VM',
      currentSpecs: 'Current Specifications',
      autoSelectedType: 'Auto-selected machine type',
      resize: 'Resize',
      resizing: 'Resizing...',
      cancel: 'Cancel',
      error: {
        resizeFailed: 'Resize failed'
      }
    },
    noVMs: 'No VMs found',
    internalIP: 'Internal IP',
    externalIP: 'External IP',
    sshCommand: 'SSH Command',
    hasExternalIP: 'Has external IP, can connect directly',
    noExternalIP: 'No external IP, use IAP tunnel',
    ssmSessionManager: 'Use AWS SSM Session Manager to connect',
    sshCommandCopied: 'SSH command copied to clipboard',
    details: 'VM Details',
    attachedDisks: 'Attached Disks',
    na: 'N/A',
    gcloudComputeSsh: 'gcloud compute ssh',
    status: {
      running: 'Running',
      stopped: 'Stopped',
      terminated: 'Terminated',
      suspended: 'Suspended',
      suspending: 'Suspending',
      stopping: 'Stopping',
      unknown: 'Unknown'
    },
    onPremise: 'On-Premise'
  },

  // Disk related
  disk: {
    name: 'Disk Name',
    size: 'Size',
    zone: 'Zone',
    type: 'Type',
    attached: 'Attached',
    detached: 'Detached',
    attachedDisk: 'Attached Disk',
    detachDisk: 'Detach Disk',
    deleteDisk: 'Delete Disk',
    editLabels: 'Edit Labels',
    noDisks: 'No disks found',
    attachedVM: 'Attached VM',
    details: 'Disk Details',
    ready: 'Ready',
    creating: 'Creating',
    restoring: 'Restoring',
    unknown: 'Unknown',
    systemDisk: 'System Disk',
    status: {
      ready: 'Ready',
      creating: 'Creating',
      restoring: 'Restoring',
      shuttingDown: 'Shutting Down',
      unknown: 'Unknown',
      pending: 'Pending',
      available: 'Available',
      attached: 'Attached'
    },
    error: {
      missingZone: 'Cannot attach disk: missing zone information.'
    },
    attachedTo: 'Attached To',
    device: 'Device',
    notAttached: 'Not Attached',
    na: 'N/A'
  },

  // Labels
  labels: {
    title: 'Labels',
    owner: 'Owner',
    team: 'Team',
    org: 'Organization',
    editLabels: 'Edit Labels',
    saveLabels: 'Save Labels',
    labelsUpdated: 'Labels updated',
    ownerPlaceholder: 'Enter owner',
    teamPlaceholder: 'Enter team',
    orgPlaceholder: 'Enter organization',
    noLabels: 'No labels',
    manageLabels: 'Label Management',
    currentLabels: 'Current Labels',
    invalidEmail: 'Please enter a valid email address'
  },

  // Create VM modal
  createVM: {
    title: 'Create Virtual Machine',
    name: 'Name',
    machineType: 'Machine Type',
    zone: 'Zone',
    bootDisk: 'Boot Disk',
    bootDiskSize: 'Boot Disk Size (GB)',
    network: 'Network',
    subnetwork: 'Subnetwork',
    externalIP: 'External IP',
    allowHTTP: 'Allow HTTP Traffic',
    allowHTTPS: 'Allow HTTPS Traffic',
    create: 'Create',
    cancel: 'Cancel',
    nameRequired: 'Please enter name',
    machineTypeRequired: 'Please select machine type',
    zoneRequired: 'Please select zone',
    bootDiskSizeRequired: 'Please enter boot disk size',
    creating: 'Creating...',
    created: 'Virtual machine created successfully',
    error: 'Failed to create virtual machine',
    gpuType: 'GPU Type',
    gpuCount: 'GPU Count',
    image: 'Image',
    serviceAccount: 'Service Account',
    estimatedMonthlyCost: 'Estimated Monthly Cost',
    createFailed: 'Failed to create VM',
    monthlyCostEstimate: 'Monthly Cost Estimate',
    computeResource: 'Compute Resource',
    gpuResource: 'GPU ({0}, {1})',
    storageResource: 'Storage ({size}GB)',
    total: 'Total',
    month: 'month',
    selectImage: 'Select Image',
    defaultServiceAccount: 'Default (Compute Engine default service account)',
    a100AutoMatch: 'A100 GPU 40G automatically matches A2 High GPU machine',
    gpuTypes: {
      none: 'No GPU'
    },
    machineTypeGroups: {
      e2Series: 'E2 Series (Cost-effective)',
      e2Standard: 'E2 Standard',
      e2HighCpu: 'E2 High CPU',
      n1Standard: 'N1 Standard',
      g2StandardL4: 'G2 Standard (L4 GPU)',
      n1StandardT4: 'N1 Standard (T4 GPU)'
    },
    // AWS related translations
    region: 'Region',
    instanceType: 'Instance Type',
    instanceTypeRequired: 'Please select instance type',
    volumeType: 'Volume Type'
  },

  // Create disk modal
  createDisk: {
    title: 'Create Disk',
    name: 'Name',
    size: 'Size (GB)',
    type: 'Type',
    zone: 'Zone',
    create: 'Create',
    cancel: 'Cancel',
    nameRequired: 'Please enter name',
    sizeRequired: 'Please enter size',
    typeRequired: 'Please select type',
    zoneRequired: 'Please select zone',
    creating: 'Creating...',
    created: 'Disk created successfully',
    error: 'Failed to create disk',
    estimatedMonthlyCost: 'Estimated Monthly Cost',
    createFailed: 'Failed to create disk',
    types: {
      standard: 'Standard Persistent Disk',
      ssd: 'SSD Persistent Disk',
      balanced: 'Balanced Persistent Disk'
    }
  },

  // Audit log
  audit: {
    title: 'Audit Log',
    project: 'Project',
    action: 'Action',
    resource: 'Resource',
    user: 'User',
    timestamp: 'Timestamp',
    details: 'Details',
    filter: 'Filter',
    clear: 'Clear',
    noLogs: 'No logs found',
    selectProject: 'Select Project',
    searchPlaceholder: 'Search by user, resource name...',
    allActions: 'All Actions',
    allStatus: 'All Status',
    success: 'Success',
    failed: 'Failed',
    pending: 'Pending',
    allResourceTypes: 'All Resource Types',
    allProjects: 'All Projects',
    timeRange: 'Time Range',
    export: 'Export',
    last7Days: 'Last 7 Days',
    last30Days: 'Last 30 Days',
    last90Days: 'Last 90 Days',
    custom: 'Custom',
    to: 'to',
    totalRecords: 'Total Records',
    successfulOperations: 'Successful Operations',
    failedOperations: 'Failed Operations',
    activeUsers: 'Active Users',
    time: 'Time',
    operator: 'Operator',
    actionType: 'Action Type',
    resourceName: 'Resource Name',
    resourceType: 'Resource Type',
    projectId: 'Project ID',
    status: 'Status',
    duration: 'Duration',
    noMatchingLogs: 'No matching audit logs found',
    clearFilters: 'Clear Filters',
    pageInfo: 'Page {current} of {total} ({records} records)',
    logDetails: 'Audit Log Details',
    basicInfo: 'Basic Information',
    operationTime: 'Operation Time',
    zone: 'Zone',
    ipAddress: 'IP Address',
    detailedInfo: 'Detailed Information',
    errorInfo: 'Error Information',
    fetchLogsFailed: 'Failed to fetch audit logs',
    fetchStatsFailed: 'Failed to fetch statistics',
    actions: {
      vmCreate: 'Create VM',
      vmStart: 'Start VM',
      vmStop: 'Stop VM',
      vmDelete: 'Delete VM',
      diskCreate: 'Create Disk',
      diskDelete: 'Delete Disk',
      diskAttach: 'Attach Disk',
      diskDetach: 'Detach Disk'
    },
    statuses: {
      success: 'Success',
      failed: 'Failed',
      pending: 'Pending'
    }
  },

  // Notifications
  notifications: {
    operationSuccess: 'Operation successful',
    operationFailed: 'Operation failed',
    confirmDelete: 'Confirm deletion?',
    deleteSuccess: 'Delete successful',
    deleteFailed: 'Delete failed',
    attachSuccess: 'Attach successful',
    attachFailed: 'Attach failed',
    detachSuccess: 'Detach successful',
    detachFailed: 'Detach failed'
  },

  // Subregion
  subregion: {
    title: 'Subregion',
    unknown: 'Unknown Subregion'
  },

  // Language switching
  language: {
    zh: '中文',
    en: 'English',
    ja: '日本語',
    switchLanguage: 'Switch Language'
  },

  // Site switching
  siteSwitcher: {
    title: 'Site Switcher',
    currentSite: 'Current Site',
    switchSite: 'Switch Site',
    awsSite: 'AWS Manager',
    gcpSite: 'GCP Manager',
    awsUrl: 'ec2-manager.robotruck.internal.jp',
    gcpUrl: 'gce-manager.robotruck.internal.jp',
    switching: 'Switching...',
    switchConfirm: 'Are you sure you want to switch to {site}?',
    switchSuccess: 'Successfully switched to {site}',
    switchFailed: 'Failed to switch site'
  },

  // Team
  team: {
    siDev: 'SI Dev',
    algoDev: 'Algo Dev',
    infraDev: 'Infra Dev',
    hi: 'HI'
  },

  // Organization
  org: {
    engineering: 'Engineering',
    product: 'Product'
  },

  // Composables
  composables: {
    // Error messages
    errors: {
      loadResourcesFailed: 'Failed to load resources',
      loadProjectsFailed: 'Failed to get project list',
      operationFailed: 'Operation failed',
      deleteFailed: 'Delete failed',
      attachFailed: 'Attach failed',
      detachFailed: 'Detach failed',
      createFailed: 'Create failed',
      loadProjectFirst: 'Please load project resources first',
      fetchUserFailed: 'Failed to fetch user data'
    },
    // Confirmation messages
    confirm: {
      startVM: 'Are you sure you want to start this virtual machine?',
      stopVM: 'Are you sure you want to stop this virtual machine?',
      deleteVM: 'Are you sure you want to delete this virtual machine? This action cannot be undone!',
      resizeVM: 'Are you sure you want to resize this virtual machine?',
      deleteDisk: 'Are you sure you want to delete this disk? This action cannot be undone!',
      detachDisk: 'Are you sure you want to detach disk {diskName} from VM {vmName}?'
    },
    // Status
    status: {
      running: 'Running',
      stopped: 'Stopped',
      terminated: 'Terminated',
      unknown: 'Unknown',
      ready: 'Ready',
      creating: 'Creating',
      restoring: 'Restoring'
    },
    // Log messages
    logs: {
      connectedToServer: 'Connected to server',
      disconnectedFromServer: 'Disconnected from server',
      websocketError: 'WebSocket error',
      vmOperationFailed: 'VM operation failed',
      deleteDiskFailed: 'Delete disk failed',
      attachDiskFailed: 'Attach disk failed',
      detachDiskFailed: 'Detach disk failed',
      createDiskFailed: 'Create disk failed',
      cannotGetVMMonitoring: 'Cannot get monitoring data for VM {vmName}',
      cannotGetDiskMonitoring: 'Cannot get monitoring data for disk {diskName}',
      userNotAuthenticated: 'User not authenticated via IAP',
      userAuthenticated: 'User authenticated'
    },
    // Notifications
    notifications: {
      autoRemove: 'Auto remove notification'
    }
  }
};
